import CallbackFactory, { Callback, CallbackRegistry, UnsubscribeFunction } from "callback-registry";
import { Glue42Core } from "@glue42/core";
import { Glue42 } from "../../glue";
import {
    Executor,
    FrameButton,
    Neighbours,
    WindowConstructorOptions,
    WindowEvents,
    WindowWrapper,
    FlashOptions,
    DetachTabOptions,
    FlashTabOptions,
    GroupCreationArgs,
} from "./types";
import * as util from "../activity/helpers/util";
import utils from "../utils";

import { default as windowStore } from "./store";
import GDWindow = Glue42.Windows.GDWindow;
import { CompositionChangedData } from "./environment/gd/types";
import JumpList from "./jumpList/main";

export default (id: string, options: WindowConstructorOptions, executor: Executor, logger: Glue42Core.Logger.API, appManagerGetter: () => Glue42.AppManager.API, displayAPIGetter: () => Glue42.Displays.API, channelsAPIGetter: () => Glue42.Channels, agm: Glue42.AGM.API): WindowWrapper => {
    const _registry: CallbackRegistry = CallbackFactory();
    const getChannels = () => {
        const channels = channelsAPIGetter();
        if (!channels) {
            throw new Error(`To use this method you need to enable channels API - set the channels property to true when initializing the Glue42 library`);
        }
        return channels;
    };

    const _id = id;
    const _name: string = options.name;
    const _mode: Glue42.Windows.WindowMode = options.mode;
    let _bounds: Glue42.Windows.Bounds = options.bounds;
    let _url: string = options.url;
    let _title: string = options.title;
    let _context: any = options.context ?? {};
    let _frameColor: string = options.frameColor;
    let _focus: boolean = options.focus;
    let _neighbours: Neighbours = options.neighbours ?? {};
    let _groupId: string = options.groupId;
    let _isGroupHeaderVisible: boolean = options.isGroupHeaderVisible;
    let _isGroupHibernated: boolean = options.isGroupHibernated;
    let _isGroupVisible: boolean = options.isGroupVisible;
    let _isTabHeaderVisible: boolean = options.isTabHeaderVisible;
    let _isTabSelected: boolean = options.isTabSelected ?? false;
    let _settings: Glue42.Windows.WindowSettings = options.settings;
    const _applicationName = options.applicationName;

    // is Window visible
    let _isVisible: boolean = options.isVisible;

    // is Window sticky enable
    let _isSticky: boolean = options.isSticky;

    // Window state Collapsed | Expanded
    let _isCollapsed: boolean = options.isCollapsed;

    // Window state normal | maximized | minimized
    let _windowState: Glue42.Windows.WindowState = options.state;

    // tabGroupId only for window with 'Tab' mode
    let _tabGroupId: string = options.tabGroupId;
    let _tabIndex: number = options.tabIndex;

    let _frameId: string = options.frameId;
    // window is locked or unlocked;
    let _isLocked: boolean = options.isLocked;

    let _group: Glue42.Windows.Group;

    // Holding all frame buttons
    let _frameButtons: Glue42.Windows.ButtonInfo[] = options.frameButtons ?? [];

    // Holding the current zoom factor
    let _zoomFactor = options.zoomFactor;

    let _placementSettings: Glue42.Windows.PlacementSettings = options.placementSettings;

    const _jumpList: JumpList = new JumpList(id);

    // #region "AGM methods"
    function close(closeOptions?: Glue42.Windows.CloseOptions): Promise<boolean>;
    function close(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow>;
    function close(cbOrOptions?: Glue42.Windows.CloseOptions | ((window: Glue42.Windows.GDWindow) => void), error?: (error: string) => void): Promise<Glue42.Windows.GDWindow | boolean> {
        if (typeof cbOrOptions === "undefined" || typeof cbOrOptions === "function") {
            return utils.callbackifyPromise(() => {
                if (!id) {
                    throw new Error("The window is already closed.");
                }
                return executor.close(resultWindow);
            }, cbOrOptions, error);
        } else {
            return executor.close(resultWindow, cbOrOptions);
        }
    }

    function navigate(newUrl: string, loadURLOptions: Glue42.Windows.LoadURLOptions): Promise<Glue42.Windows.GDWindow>;
    function navigate(newUrl: string, success?: (window: GDWindow) => void, error?: (error: string) => void): Promise<GDWindow>;
    function navigate(newUrl: string, optionsOrCallback: Glue42.Windows.LoadURLOptions | ((window: Glue42.Windows.GDWindow) => void), error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        if (typeof optionsOrCallback === "function") {
            return utils.callbackifyPromise(() => {
                if (util.isNullOrWhiteSpace(newUrl)) {
                    throw new Error("The new URL must be a non-empty string.");
                }
                return executor.navigate(resultWindow, newUrl);
            }, optionsOrCallback, error);
        }
        if (util.isNullOrWhiteSpace(newUrl)) {
            throw new Error("The new URL must be a non-empty string.");
        }
        return executor.navigate(resultWindow, newUrl, optionsOrCallback);
    }

    function setStyle(style: Glue42.Windows.WindowStyle, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (!style || Object.keys(style).length === 0 || Object.keys(style).every((key) => !key)) {
                throw new Error("Invalid style arguments: " + JSON.stringify(style));
            }
            if (style && style.focus !== undefined) {
                if (typeof style.focus !== "boolean") {
                    throw new Error("Focus must be a boolean value. Currently, only `focus: true` is supported.");
                } else if (style.focus === false) {
                    // eslint-disable-next-line no-console
                    console.warn("`focus: false` is not supported!");
                }
            }
            if (style && style.hidden !== undefined && typeof style.hidden !== "boolean") {
                throw new Error("The `hidden` property must hold a boolean value.");
            }
            for (const prop of ["minHeight", "maxHeight", "minWidth", "maxWidth"]) {
                const styleAsAny = style as any;
                const value = styleAsAny[prop];
                if (prop in style) {
                    if (util.isUndefinedOrNull(value)) {
                        delete styleAsAny[prop];
                        continue;
                    }
                    if (!util.isNumber(styleAsAny[prop])) {
                        throw new Error(`"${prop}" must be a number`);
                    }
                }
            }
            return executor.setStyle(resultWindow, style);
        }, success, error);
    }

    function resetButtons(buttons: Glue42.Windows.WindowButtons, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(
            () => executor.resetButtons(resultWindow, buttons),
            success,
            error
        );
    }

    function getButtons(): Promise<Glue42.Windows.WindowButtons> {
        return executor.getButtons(resultWindow);
    }

    function setOnTop(onTop: boolean | "always", success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (typeof onTop === "string") {
                if (onTop !== "always") {
                    throw new Error("`onTop` must hold a `always` value.");
                }
            } else if (typeof onTop !== "boolean") {
                throw new Error("`onTop` must hold a boolean or `always` value.");
            }
            return executor.setOnTop(resultWindow, onTop);
        }, success, error);
    }

    function setSizeConstraints(constraints: Glue42.Windows.SizeConstraints, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (!constraints || Object.keys(constraints).every((value) => value === undefined)) {
                throw new Error("The properties of `constraints` cannot be null or undefined.");
            }
            return executor.setSizeConstraints(resultWindow, constraints);
        }, success, error);
    }

    function getSizeConstraints(): Promise<Glue42.Windows.SizeConstraints> {
        return executor.getSizeConstraints(resultWindow);
    }

    function setTitle(newTitle: string, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isUndefinedOrNull(newTitle)) {
                throw new Error("`newTitle` must not be null or undefined.");
            }
            if (newTitle === _title) {
                return Promise.resolve(resultWindow);
            }
            return executor.setTitle(resultWindow, newTitle);
        }, success, error);
    }

    function setSticky(isSticky: boolean, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (typeof isSticky !== "boolean") {
                throw new Error("`isSticky` must hold a boolean value.");
            }
            return executor.setSticky(resultWindow, isSticky);
        }, success, error);
    }

    function moveResize(bounds: Partial<Glue42.Windows.Bounds>, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isUndefinedOrNull(bounds)) {
                throw new Error("The properties of `bounds` cannot be null or undefined.");
            }
            return executor.moveResize(resultWindow, bounds);
        }, success, error);
    }

    function addFrameButton(buttonInfo: Glue42.Windows.ButtonInfo, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (typeof buttonInfo === "undefined" || Object.keys(buttonInfo).length === 0) {
                throw new Error("Button info is not available.");
            }
            if (util.isNullOrWhiteSpace(buttonInfo.buttonId)) {
                throw new Error("`buttonId` must not be null or undefined.");
            }
            if (util.isNullOrWhiteSpace(buttonInfo.imageBase64)) {
                throw new Error("`imageBase64` must not be null or undefined.");
            }
            return executor.addFrameButton(resultWindow, buttonInfo);
        }, success, error);
    }

    function removeFrameButton(buttonId: string, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isNullOrWhiteSpace(buttonId)) {
                throw new Error("`buttonId` must not be null or undefined.");
            }
            return executor.removeFrameButton(resultWindow, buttonId);
        }, success, error);
    }

    function activate(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (_focus) {
                return Promise.resolve(resultWindow);
            }
            return executor.activate(resultWindow);
        }, success, error);
    }

    function focus(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (_focus) {
                return Promise.resolve(resultWindow);
            }
            return executor.focus(resultWindow);
        }, success, error);
    }

    function maximizeRestore(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            return executor.maximizeRestore(resultWindow);
        }, success, error);
    }

    function maximize(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (_windowState === "maximized") {
                return Promise.resolve(resultWindow);
            }
            return executor.maximize(resultWindow);
        }, success, error);
    }

    function restore(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (_windowState === "normal") {
                return Promise.resolve(resultWindow);
            }
            return executor.restore(resultWindow);
        }, success, error);
    }

    function minimize(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (_windowState === "minimized") {
                return Promise.resolve(resultWindow);
            }
            return executor.minimize(resultWindow);
        }, success, error);
    }

    function collapse(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (_isCollapsed) {
                return Promise.resolve(resultWindow);
            }
            return executor.collapse(resultWindow);
        }, success, error);
    }

    function expand(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (!_isCollapsed) {
                return Promise.resolve(resultWindow);
            }
            return executor.expand(resultWindow);
        }, success, error);
    }

    function toggleCollapse(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            return executor.toggleCollapse(resultWindow);
        }, success, error);
    }

    function snap(target: string | Glue42.Windows.GDWindow, direction: Glue42.Windows.RelativeDirection, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow>;
    function snap(target: string | Glue42.Windows.GDWindow, snappingOptions: Glue42.Windows.SnappingOptions, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow>;
    function snap(target: string | Glue42.Windows.GDWindow, direction: Glue42.Windows.RelativeDirection | Glue42.Windows.SnappingOptions, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isUndefinedOrNull(target)) {
                throw new Error(`A target window is not specified - ${typeof target === "string" ? target : JSON.stringify(target)}`);
            }

            if (typeof target === "string") {
                const win = windowStore.get(target);
                if (!win) {
                    throw new Error(`Invalid "target" parameter or no such window. Invoked with: ${target}`);
                }
                target = win.API;
            }
            if (typeof direction === "string") {
                direction = {
                    direction,
                    autoAlign: true
                };
            }
            return executor.snap(resultWindow, target, direction);
        }, success, error);
    }

    function attachTab(tab: Glue42.Windows.GDWindow | string, opt: number | Glue42.Windows.AttachOptions, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            const errorMessage = `Invalid "tab" parameter - must be an object with an "id" property or a string. Invoked for source window with ID:`;
            if (util.isUndefinedOrNull(tab)) {
                const errMsg = `${errorMessage} ${typeof tab === "string" ? tab : JSON.stringify(tab)}`;
                throw new Error(errMsg);
            }
            // tab === sourceWindow
            let sourceWindow;
            if (typeof tab === "string") {
                sourceWindow = windowStore.get(tab)?.API;
                if (util.isUndefinedOrNull(sourceWindow)) {
                    const errMsg = `${errorMessage} ${typeof sourceWindow === "string" ? sourceWindow : JSON.stringify(sourceWindow)}`;
                    throw new Error(errMsg);
                }
            } else if (!util.isUndefinedOrNull(tab.id)) {
                sourceWindow = tab;
            } else {
                throw new Error(errorMessage);
            }
            const attachOptions: Glue42.Windows.AttachOptions = {};
            if (!util.isUndefinedOrNull(opt)) {
                if (typeof opt === "number") {
                    attachOptions.index = opt;
                } else {
                    attachOptions.selected = opt.selected;
                    attachOptions.index = opt.index;
                }
            }

            return executor.attachTab(resultWindow, sourceWindow, attachOptions);
        }, success, error);
    }

    function detachTab(opt: Glue42.Windows.DetachOptions = {}, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            const argsForSend: DetachTabOptions = {};
            function isDetachRelative(o: Glue42.Windows.DetachOptions) {
                return o.relativeTo !== undefined;
            }
            if (isDetachRelative(opt)) {
                if (typeof opt.relativeTo === "string") {
                    argsForSend.relativeTo = opt.relativeTo;
                } else if (!util.isUndefinedOrNull(opt.relativeTo.id)) {
                    argsForSend.relativeTo = opt.relativeTo.id;
                }
                if (!util.isUndefinedOrNull(opt.relativeDirection)) {
                    argsForSend.relativeDirection = opt.relativeDirection;
                }
                if (!util.isUndefinedOrNull(opt.width)) {
                    argsForSend.width = opt.width;
                }
                if (!util.isUndefinedOrNull(opt.height)) {
                    argsForSend.height = opt.height;
                }
            } else {
                if (!util.isUndefinedOrNull(opt.bounds)) {
                    argsForSend.bounds = opt.bounds;
                }
            }
            if (!util.isUndefinedOrNull(opt.hideTabHeader)) {
                argsForSend.hideTabHeader = opt.hideTabHeader;
            }
            return executor.detachTab(resultWindow, argsForSend);
        }, success, error);
    }

    function setVisible(toBeVisible?: boolean, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            return executor.setVisible(resultWindow, toBeVisible);
        }, success, error);
    }

    async function center(display?: Glue42.Displays.Display) {
        if (display) {
            validateCenterArguments(display);
        }
        return executor.center(resultWindow, display);
    }

    function validateCenterArguments(display?: Glue42.Displays.Display) {
        if (typeof display !== "object") {
            throw Error("display argument must be a valid display object");
        }
        if (!display.workArea || !display.scaleFactor) {
            throw Error("display argument is not a valid display object");
        }
    }
    function showLoader(loader?: Glue42.Windows.Loader): Promise<Glue42.Windows.GDWindow> {
        return executor.showLoader(resultWindow, loader);
    }

    function hideLoader(): Promise<Glue42.Windows.GDWindow> {
        return executor.hideLoader(resultWindow);
    }

    function updateContext(context: any, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isUndefinedOrNull(context)) {
                throw new Error(`"context" must not be null or undefined.`);
            }

            return executor.updateContext(resultWindow, context, false);
        }, success, error);
    }

    function lock(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            return executor.lock(resultWindow);
        }, success, error);
    }

    function unlock(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            return executor.unlock(resultWindow);
        }, success, error);
    }

    function getIcon(success?: (icon: string) => void, error?: (error: string) => void): Promise<string> {
        return utils.callbackifyPromise(() => {
            return executor.getIcon(resultWindow);
        }, success, error);
    }

    function setIcon(base64Image: string, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isNullOrWhiteSpace(base64Image)) {
                throw new Error(`"base64Image" must be a non-empty string.`);
            }
            return executor.setIcon(resultWindow, base64Image);
        }, success, error);
    }

    function setFrameColor(frameColor: string, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (util.isNullOrWhiteSpace(frameColor)) {
                throw new Error(`"frameColor" must be a non-empty string`);
            }
            return executor.setFrameColor(resultWindow, frameColor);
        }, success, error);
    }

    function setTabHeaderVisible(toBeTabHeaderVisible: boolean, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise(() => {
            if (typeof toBeTabHeaderVisible !== "boolean") {
                throw new Error(`"toBeTabHeaderVisible" must hold a boolean value.`);
            }
            return executor.setTabHeaderVisible(resultWindow, toBeTabHeaderVisible);
        }, success, error);
    }

    async function setTabTooltip(tooltip: string): Promise<Glue42.Windows.GDWindow> {
        if (util.isNullOrWhiteSpace(tooltip)) {
            throw new Error(`"${tooltip}" must not be null or undefined`);
        }
        return executor.setTabTooltip(resultWindow, tooltip);
    }

    async function getTabTooltip(): Promise<string> {
        return executor.getTabTooltip(resultWindow);
    }

    function showPopup(config: Glue42.Windows.PopupOptions): Promise<Glue42.Windows.GDWindow> {
        return executor.showPopup(resultWindow, config);
    }

    function createFlydown(config: Glue42.Windows.FlydownOptions): Promise<Glue42.Windows.Flydown> {
        return executor.createFlydown(resultWindow.id, config);
    }

    function setModalState(isModal: boolean): Promise<void> {
        return executor.setModalState(resultWindow.id, isModal || false);
    }

    function zoomIn(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise<Glue42.Windows.GDWindow>(() => {
            return executor.zoomIn(resultWindow);
        }, success, error);
    }

    function zoomOut(success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise<Glue42.Windows.GDWindow>(() => {
            return executor.zoomOut(resultWindow);
        }, success, error);
    }

    function setZoomFactor(zoomFactor: number, success?: (window: Glue42.Windows.GDWindow) => void, error?: (error: string) => void): Promise<Glue42.Windows.GDWindow> {
        return utils.callbackifyPromise<Glue42.Windows.GDWindow>(() => {
            if (isNaN(zoomFactor)) {
                throw new Error(`zoomFactor is not a number`);
            }
            return executor.setZoomFactor(resultWindow, zoomFactor);
        }, success, error);
    }

    function showDevTools() {
        return executor.showDevTools(resultWindow);
    }

    function capture(captureOptions: Glue42.Windows.CaptureOptions): Promise<string> {
        return executor.capture(resultWindow, captureOptions);
    }

    function flash(suppliedOptions?: boolean, mode?: Glue42.Windows.FlashMode): Promise<Glue42.Windows.GDWindow> {
        const flashOptions: FlashOptions = {
            shouldFlash: true,
            mode: "auto"
        };

        if (typeof suppliedOptions === "boolean") {
            flashOptions.shouldFlash = suppliedOptions;
        }

        if (typeof mode !== "undefined") {
            flashOptions.mode = mode;
        }

        return executor.flash(resultWindow, flashOptions);
    }

    function flashTab(suppliedOptions?: boolean): Promise<Glue42.Windows.GDWindow> {
        const flashOptions: FlashTabOptions = {
            shouldFlash: true,
        };

        if (typeof suppliedOptions === "boolean") {
            flashOptions.shouldFlash = suppliedOptions;
        }

        return executor.flashTab(resultWindow, flashOptions);
    }

    function print(printOptions: Glue42.Windows.PrintOptions): Promise<Glue42.Windows.GDWindow> {
        return executor.print(resultWindow, printOptions);
    }

    function printToPDF(printToPDFOptions: Glue42.Windows.PrintToPDFOptions): Promise<string> {
        return executor.printToPDF(resultWindow, printToPDFOptions);
    }

    function ungroup(ungroupOptions?: Glue42.Windows.UngroupOptions): Promise<Glue42.Windows.GDWindow> {
        return new Promise((resolve, reject) => {
            const unGroupChanged = onGroupChanged(
                (win: Glue42.Windows.GDWindow, newGroup: Glue42.Windows.Group, oldGroup: Glue42.Windows.Group) => {
                    if (id === win.id) {
                        unGroupChanged();
                        resolve(resultWindow);
                    }
                });

            executor.ungroup(resultWindow, ungroupOptions)
                .catch((e: string) => {
                    unGroupChanged();
                    reject(e);
                });
        });
    }

    function place(placementSettings: Glue42.Windows.PlacementSettings): Promise<void> {
        return executor.place(resultWindow, placementSettings);
    }

    function refresh(ignoreCache: boolean) {
        return executor.refresh(resultWindow, ignoreCache);
    }

    function download(url: string, opts: Glue42.Windows.DownloadOptions) {
        return executor.download(resultWindow, url, opts);
    }

    function configure(settings: Glue42.Windows.WindowConfiguration) {
        return executor.configureWindow(resultWindow, settings);
    }

    function getConfiguration() {
        return executor.getWindowConfiguration(resultWindow);
    }

    // #endregion "AGM methods"

    // #region "Events"

    function onTitleChanged(callback: (title: string, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        if (!util.isFunction(callback)) {
            throw new Error("callback should be a function");
        }
        callback(resultWindow.title, resultWindow);
        return onEventCore("onTitleChanged", callback);
    }

    function onClose(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        if (!util.isFunction(callback)) {
            throw new Error("callback should be a function");
        }
        if (id === undefined) {
            callback(resultWindow);
        }
        return _registry.add("onClose", callback);
    }

    function onUrlChanged(callback: (url: string, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("onUrlChanged", callback);
    }

    function onFrameButtonAdded(callback: (btnInfo: Glue42.Windows.ButtonInfo, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("onFrameButtonAdded", callback);
    }

    function onFrameButtonRemoved(callback: (btnInfo: Glue42.Windows.ButtonInfo, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("onFrameButtonRemoved", callback);
    }

    function onFrameButtonClicked(callback: (btnInfo: Glue42.Windows.ButtonInfo, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("onFrameButtonClicked", callback);
    }

    function onCollapsed(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        if (!util.isFunction(callback)) {
            throw new Error("callback should be a function");
        }
        if (_isCollapsed) {
            callback(resultWindow);
        }
        return _registry.add("collapsed", callback);
    }

    function onExpanded(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        if (!util.isFunction(callback)) {
            throw new Error("callback should be a function");
        }
        if (!_isCollapsed) {
            callback(resultWindow);
        }
        return _registry.add("expanded", callback);
    }

    function onMaximized(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        if (_windowState === "maximized") {
            return onEventCore("maximized", callback, [resultWindow]);
        } else {
            return onEventCore("maximized", callback);
        }
    }

    function onMinimized(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        if (_windowState === "minimized") {
            return onEventCore("minimized", callback, [resultWindow]);
        } else {
            return onEventCore("minimized", callback);
        }
    }

    function onNormal(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        if (_windowState === "normal") {
            return onEventCore("normal", callback, [resultWindow]);
        } else {
            return onEventCore("normal", callback);
        }
    }

    function onAttached(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("attached", callback);
    }

    function onDetached(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("detached", callback);
    }

    function onVisibilityChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("visibility-changed", callback);
    }

    function onContextUpdated(callback: (context: any, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("context-updated", callback);
    }

    function onLockingChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("lock-changed", callback);
    }

    function onBoundsChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("bounds-changed", callback);
    }

    function onFocusChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("focus-changed", callback);
    }

    function onStickyChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("sticky-changed", callback);
    }

    function onFrameColorChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("frame-color-changed", callback);
    }

    function onTabHeaderVisibilityChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("tab-header-visibility-changed", callback);
    }

    function onWindowAttached(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("window-attached", callback);
    }

    function onWindowDetached(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("window-detached", callback);
    }

    function onGroupChanged(callback: (window: Glue42.Windows.GDWindow, newGroup: Glue42.Windows.Group, oldGroup: Glue42.Windows.Group) => void): UnsubscribeFunction {
        return onEventCore("group-changed", callback);
    }

    function onTabSelectionChanged(callback: (selectedWindow: Glue42.Windows.GDWindow, previousWindow: Glue42.Windows.GDWindow, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("tab-selection-changed", callback);
    }

    function onClosing(callback: (prevent: (options: Glue42.Windows.PreventClosingOptions) => void) => Promise<void>): UnsubscribeFunction {
        if (!util.isFunction(callback)) {
            throw new Error("callback must be a function");
        }

        type GDHandler = (success: () => void, error: () => void, prevent: (options: Glue42.Windows.PreventClosingOptions) => void) => void;
        const callbackWrap: GDHandler = (success: () => void, error: () => void, prevent: (options: Glue42.Windows.PreventClosingOptions) => void) => {
            const promise = callback(prevent);
            if (promise?.then) {
                promise.then(success).catch(error);
            } else {
                success();
            }
        };
        return executor.onClosing(callbackWrap, resultWindow);
    }

    function onRefreshing(callback: (prevent: () => void) => Promise<void>): UnsubscribeFunction {
        if (!util.isFunction(callback)) {
            throw new Error("callback must be a function");
        }

        type GDRefreshHandler = (success: () => void, error: () => void, prevent: () => void) => void;
        const callbackWrap: GDRefreshHandler = (success: () => void, error: () => void, prevent: () => void) => {
            const promise = callback(prevent);
            if (promise?.then) {
                promise.then(success).catch(error);
            } else {
                success();
            }
        };
        return executor.onRefreshing(callbackWrap, resultWindow);
    }

    function onNavigating(callback: (args: { newUrl: string }) => Promise<void>): UnsubscribeFunction {
        if (!util.isFunction(callback)) {
            throw new Error("callback must be a function");
        }

        type Handler = (success: () => void, error: () => void, prevent: () => void, args: { newUrl: string }) => void;
        const callbackWrap: Handler = (success: () => void, error: () => void, prevent: () => void, args: { newUrl: string }) => {
            const promise = callback(args);
            if (promise?.then) {
                promise.then(success).catch(error);
            } else {
                success();
            }
        };
        return executor.onNavigating(callbackWrap, resultWindow);
    }

    function onZoomFactorChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("zoom-factor-changed", callback);
    }

    function onPlacementSettingsChanged(callback: (window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("placementSettingsChanged", callback);
    }

    function onNeighboursChanged(callback: (neighbours: Glue42.Windows.Neighbours, window: Glue42.Windows.GDWindow) => void): UnsubscribeFunction {
        return onEventCore("neighbours-changed", callback);
    }

    function onEventCore(key: string, callback: Callback, replayArguments?: any[]) {
        if (!util.isFunction(callback)) {
            throw new Error("callback must be a function");
        }

        return _registry.add(key, callback, replayArguments);
    }

    function goBack() {
        return executor.goBack(resultWindow);
    }

    function goForward() {
        return executor.goForward(resultWindow);
    }

    function startDrag(option: Glue42.Windows.StartDragOptions) {
        return executor.startDrag(resultWindow, option);
    }

    function showDialog<T>(dialogOptions: Glue42.Windows.DialogOptions): Promise<T> {
        if (dialogOptions?.timerDuration && isNaN(dialogOptions?.timerDuration)) {
            throw new Error("timerDuration must be a number");
        }
        if (dialogOptions?.showTimer && typeof dialogOptions?.showTimer !== "boolean") {
            throw new Error("showTimer must be a boolean");
        }
        return executor.showDialog(resultWindow, dialogOptions);
    }

    // #endregion "Events"

    // #region "Stream function"

    function handleUpdate(updated: WindowConstructorOptions) {
        _url = updated.url;
        _title = updated.title;
        _context = updated.context || {};
        _bounds = updated.bounds;
        _frameColor = updated.frameColor;
        _focus = updated.focus;
        _neighbours = updated.neighbours || {};
        _groupId = updated.groupId;
        _isGroupHeaderVisible = updated.isGroupHeaderVisible;
        _isGroupHibernated = updated.isGroupHibernated;
        _isGroupVisible = updated.isGroupVisible;
        _isTabHeaderVisible = updated.isTabHeaderVisible;
        _isTabSelected = updated.isTabSelected;
        _settings = updated.settings;
        _isVisible = updated.isVisible;
        _isSticky = updated.isSticky;
        _isCollapsed = updated.isCollapsed;
        _windowState = updated.state;
        _tabGroupId = updated.tabGroupId;
        _frameId = updated.frameId;
        _isLocked = updated.isLocked;
        // _frameButtons = updated.fra;
        // _group: Glue42.Windows.Group;
        _zoomFactor = updated.zoomFactor;
        _placementSettings = updated.placementSettings;
    }

    function handleTitleChanged(newTitle: string): void {
        _title = newTitle;
        executor.finished
            .finally(() => {
                _registry.execute("onTitleChanged", newTitle, resultWindow);
            });
    }

    function handleUrlChanged(newUrl: string): void {
        _url = newUrl;
        _registry.execute("onUrlChanged", newUrl, resultWindow);
    }

    function handleVisibilityChanged(isVisible: boolean) {
        if (isVisible === _isVisible) {
            return;
        }
        _isVisible = isVisible;
        _registry.execute("visibility-changed", resultWindow);
    }

    function handleWindowSettingsChanged(settings: Glue42.Windows.WindowSettings): void {
        _settings = settings;
        _registry.execute("settings-changed", resultWindow);
    }

    function handleContextUpdated(context: any): void {
        _context = context;
        _registry.execute("context-updated", _context, resultWindow);
    }

    function handleWindowClose(): void {
        if (id === undefined) {
            return;
        }
        _registry.execute("onClose", resultWindow);
        id = undefined;
    }

    function handleFrameButtonAdded(frameButton: FrameButton): void {
        const buttonObj = ["buttonId", "imageBase64", "order", "tooltip"].reduce((memo: any, k) => {
            memo[k] = frameButton[k];
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return memo;
        }, {}) as Glue42.Windows.ButtonInfo;

        const frameButtonsIds = _frameButtons.map((btn) => {
            return btn.buttonId;
        });

        if (frameButtonsIds.indexOf(frameButton.buttonId) === -1) {
            _frameButtons.push(buttonObj);
        }

        _registry.execute("onFrameButtonAdded", buttonObj, resultWindow);
    }

    function handleFrameButtonRemoved(frameButtonId: string): void {
        let button: Glue42.Windows.ButtonInfo;
        _frameButtons = _frameButtons.reduce<Glue42.Windows.ButtonInfo[]>((memo, btn) => {
            if (btn.buttonId === frameButtonId) {
                button = btn;
            } else {
                memo.push(btn);
            }
            return memo;
        }, []);

        if (button !== undefined) {
            _registry.execute("onFrameButtonRemoved", button, resultWindow);
        }
    }

    function handleFrameButtonClicked(frameButton: FrameButton): void {
        const button = _frameButtons.filter((btn) => {
            return btn.buttonId === frameButton.buttonId;
        });

        if (button.length > 0) {
            _registry.execute("onFrameButtonClicked", button[0], resultWindow);
        }
    }

    async function handleWindowChangeState(state: string): Promise<void> {
        if (state === "collapsed") {
            _isCollapsed = true;
        } else if (state === "expanded") {
            _isCollapsed = false;
        } else {
            _windowState = state as Glue42.Windows.WindowState;
        }
        await executor.finished;
        _registry.execute(state, resultWindow);
    }

    function handleFrameIsLockedChanged(isLocked: boolean): void {
        _isLocked = isLocked;
        _registry.execute("lock-changed", resultWindow);
    }

    function handleBoundsChanged(bounds: Glue42.Windows.Bounds): void {
        if (_bounds.top === bounds.top && _bounds.left === bounds.left && _bounds.width === bounds.width && _bounds.height === bounds.height) {
            return;
        }
        _bounds = bounds;
        _registry.execute("bounds-changed", resultWindow);
    }

    function handleFocusChanged(isFocused: boolean): void {
        _focus = isFocused;
        _registry.execute("focus-changed", resultWindow);
    }

    function handleIsStickyChanged(isSticky: boolean): void {
        _isSticky = isSticky;
        _registry.execute("sticky-changed", isSticky, resultWindow);
    }

    function handleFrameColorChanged(frameColor: string): void {
        _frameColor = frameColor;
        _registry.execute("frame-color-changed", resultWindow);
    }

    function handleFrameAttached(tabGroupId: string, frameId: string, isTabHeaderVisible: boolean): void {
        _tabGroupId = tabGroupId;
        _frameId = frameId;
        _isTabHeaderVisible = isTabHeaderVisible;
        _registry.execute("frame-attached", resultWindow);
    }

    function handleCompositionChanged(state: CompositionChangedData): void {
        _neighbours = state.neighbors || {};
        _tabIndex = state.index;
        _registry.execute("neighbours-changed", _neighbours, resultWindow);
    }

    function handleGroupHeaderVisibilityChanged(isGroupHeaderVisible: boolean): void {
        _isGroupHeaderVisible = isGroupHeaderVisible;
    }

    function handleTabHeaderVisibilityChanged(isTabHeaderVisible: boolean): void {
        if (_isTabHeaderVisible !== isTabHeaderVisible) {
            _isTabHeaderVisible = isTabHeaderVisible;
            _registry.execute("tab-header-visibility-changed", resultWindow);
        }
    }

    async function handleFrameSelectionChanged(newWindow: string, prevWindow: string) {
        let selectedWindow: Glue42.Windows.GDWindow;
        if (newWindow === id) {
            _isTabSelected = true;
            selectedWindow = resultWindow;
        } else {
            _isTabSelected = false;
            selectedWindow = windowStore.get(newWindow) ? windowStore.get(newWindow).API : undefined;
        }
        const previousWindow = windowStore.get(prevWindow) ? windowStore.get(prevWindow).API : undefined;
        await executor.finished;
        _registry.execute("tab-selection-changed", selectedWindow, previousWindow, resultWindow);
    }

    async function handleAttached(newTabGroupId: string, newFrameId: string, tabHeaderVisible: boolean, isLocked: boolean, winsToBeNotified: WindowWrapper[]): Promise<void> {
        _tabGroupId = newTabGroupId;
        _isTabHeaderVisible = tabHeaderVisible;
        _frameId = newFrameId;
        if (typeof isLocked !== "undefined") {
            _isLocked = isLocked;
        }
        await executor.finished;
        winsToBeNotified.forEach((w) => {
            w.Events.handleWindowAttached(resultWindow);
        });
        _registry.execute("attached", resultWindow);
    }

    function handleWindowAttached(win: Glue42.Windows.GDWindow): void {
        _registry.execute("window-attached", win);
    }

    async function handleDetached(isLocked: boolean, winsToBeNotified: WindowWrapper[]): Promise<void> {
        _tabGroupId = undefined;
        _isTabSelected = false;
        if (typeof isLocked !== "undefined") {
            _isLocked = isLocked;
        }
        await executor.finished;
        winsToBeNotified.forEach((w) => {
            w.Events.handleWindowDetached(resultWindow);
        });
        _registry.execute("detached", resultWindow);
    }

    function handleWindowDetached(win: Glue42.Windows.GDWindow): void {
        _registry.execute("window-detached", win);
    }

    function handleZoomFactorChanged(zoomFactor: number): void {
        _zoomFactor = zoomFactor;
        _registry.execute("zoom-factor-changed", resultWindow);
    }

    function handlePlacementSettingsChanged(placementSettings: Glue42.Windows.PlacementSettings): void {
        let promise: Promise<Glue42.Displays.Display>;
        const copy = placementSettings as any;
        if (!copy.display) {
            promise = Promise.resolve(undefined);
        } else {
            const displayAPI = displayAPIGetter();
            if (!displayAPI) {
                // display API is disabled
                promise = Promise.resolve(undefined);
            } else {
                const index = copy.display - 1;
                promise = new Promise((resolve, reject) => {
                    displayAPI.all().then((displays) => {
                        const display = displays.find((d) => d.index === index);
                        resolve(display);
                    }).catch(reject);
                });
            }
        }

        void promise.then((d) => {
            copy.display = d;
            _placementSettings = copy;
            _registry.execute("placementSettingsChanged", resultWindow);
        });
    }

    // #endregion "Stream function"

    // #region "Group Events"
    function handleGroupChanged(newGroup: Glue42.Windows.Group, oldGroup: Glue42.Windows.Group) {
        _group = newGroup;
        _groupId = newGroup?.id;

        if (!util.isUndefinedOrNull(newGroup) && !util.isUndefinedOrNull(oldGroup)) {
            _registry.execute("group-changed", resultWindow, newGroup, oldGroup);
        }
    }

    // #region "Internal function"

    function getAllTabs(): Glue42.Windows.GDWindow[] {
        const allWindows = windowStore.list;
        if (_mode.toLowerCase() !== "tab") {
            return [];
        }
        const tabs = Object.keys(allWindows).reduce<GDWindow[]>((memo, win) => {
            const window: WindowWrapper = allWindows[win];
            if (window
                && window.API.tabGroupId
                && typeof window.API.tabGroupId !== "undefined"
                && typeof resultWindow.tabGroupId !== "undefined"
                && window.API.tabGroupId === resultWindow.tabGroupId) {
                memo.push(window.API);
            }
            return memo;
        }, []);
        return tabs.sort((w1, w2) => {
            if (w1.tabIndex !== w2.tabIndex) {
                if (w1.tabIndex === -1) {
                    return Number.MAX_SAFE_INTEGER;
                }
                if (w2.tabIndex === -1) {
                    return Number.MIN_SAFE_INTEGER;
                }
            }
            return w1.tabIndex - w2.tabIndex;
        });
    }

    function mapWindowIdsToWindowObjects(windowIdArr: string[]): Glue42.Windows.GDWindow[] {
        return windowIdArr.reduce<Glue42.Windows.GDWindow[]>((memo, winId) => {
            const window = windowStore.get(winId);
            if (window) {
                memo.push(window.API);
            }
            return memo;
        }, []);
    }

    function getNeighboursByDirection(direction: string): Glue42.Windows.GDWindow[] {
        const windowIds = _neighbours[direction];
        if (typeof windowIds !== "undefined") {
            return mapWindowIdsToWindowObjects(windowIds);
        }
    }

    function getApplicationName(): string {
        if (_applicationName) {
            return _applicationName;
        }
        if (_context._APPLICATION_NAME) {
            return _context._APPLICATION_NAME as string;
        }
        if (_context && _context._t42 && _context._t42.application) {
            return _context._t42.application as string;
        }
        const info = getWindowInfo();
        if (info && info.applicationName) {
            return info.applicationName;
        }

        // last try (NodeJS apps)
        const appManager = appManagerGetter();
        if (appManager) {
            const instance = appManager.instances().find((i) => id === i.id);
            if (instance) {
                return instance.application?.name;
            }
        }

        return undefined;
    }

    function getWindowInfo() {
        if (typeof window !== "undefined" && window.glue42gd && window.glue42gd.getWindowInfo) {
            const info = window.glue42gd.getWindowInfo(id);
            if (!info) {
                return undefined;
            } else {
                return info;
            }
        }
    }

    // #endregion "Internal function"

    // #region "API"

    const resultWindow = {
        get id() {
            return _id;
        },
        get name() {
            return _name;
        },
        get application() {
            const appManager = appManagerGetter();
            const appName = getApplicationName();
            if (appName && appManager) {
                return appManager.application(appName);
            }
        },
        get hostInstance() {
            return executor.hostInstance;
        },
        get agmInstance() {
            const instance = agm.servers().find((s) => s.windowId === this.id);
            if (instance) {
                return instance;
            } else {
                const appName = getApplicationName();
                if (appName) {
                    return { application: appName };
                }
            }
        },
        get url() {
            return _url;
        },
        get title() {
            return _title;
        },
        get windowStyleAttributes() {
            return _settings;
        },
        get settings() {
            return _settings;
        },
        get tabGroupId() {
            return _mode.toLowerCase() === "tab" ? _tabGroupId : undefined;
        },
        get tabIndex() {
            return _mode.toLowerCase() === "tab" ? _tabIndex : undefined;
        },
        get frameId() {
            return _frameId;
        },
        get frameButtons() {
            return _frameButtons.sort((b1, b2) => b1.order - b2.order);
        },
        get mode() {
            return _mode;
        },
        get state() {
            return _windowState;
        },
        get isCollapsed() {
            return _isCollapsed;
        },
        get isVisible() {
            return _isVisible;
        },
        get isLocked() {
            return _isLocked;
        },
        get context() {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return _context;
        },
        get bounds() {
            return _bounds;
        },
        get minHeight() {
            return _settings.minHeight;
        },
        get maxHeight() {
            return _settings.maxHeight;
        },
        get minWidth() {
            return _settings.minWidth;
        },
        get maxWidth() {
            return _settings.maxWidth;
        },
        get isFocused() {
            return _focus;
        },
        get frameColor() {
            return _frameColor;
        },
        get opened() {
            return resultWindow.id !== undefined;
        },
        get group() {
            return _group;
        },
        get groupId() {
            return _groupId;
        },
        get isSticky() {
            return _isSticky;
        },
        get topNeighbours() {
            return getNeighboursByDirection("top");
        },
        get leftNeighbours() {
            return getNeighboursByDirection("left");
        },
        get rightNeighbours() {
            return getNeighboursByDirection("right");
        },
        get bottomNeighbours() {
            return getNeighboursByDirection("bottom");
        },
        get isGroupHeaderVisible() {
            return _isGroupHeaderVisible;
        },
        get activityId() {
            if (_context._t42) {
                return _context._t42.activityId as string;
            }
            const info = getWindowInfo();
            if (!info) {
                return undefined;
            }
            return info.activityId;
        },
        get activityWindowId(): string {
            if (_context._t42) {
                return _context._t42.activityWindowId as string;
            }
            const info = getWindowInfo();
            if (!info) {
                return undefined;
            }
            return info.activityWindowId;
        },
        get windowType() {
            return options.windowType || "electron";
        },
        get zoomFactor() {
            return _zoomFactor;
        },
        get screen() {
            if (typeof window !== "undefined" && window.glue42gd) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                return utils.getMonitor(resultWindow.bounds, (window.glue42gd as any).monitors);
            }
            return undefined;
        },
        get placementSettings(): Glue42.Windows.PlacementSettings {
            return Object.assign({}, _placementSettings);
        },
        get jumpList(): JumpList {
            return _jumpList;
        },
        maximize,
        restore,
        minimize,
        maximizeRestore,
        collapse,
        expand,
        toggleCollapse,
        focus,
        activate,
        moveResize,
        setTitle,
        setStyle,
        setOnTop,
        resetButtons,
        getButtons,
        setSizeConstraints,
        getSizeConstraints,
        navigate,
        addFrameButton,
        removeFrameButton,
        setVisible,
        show: () => setVisible(true),
        hide: () => setVisible(false),
        center,
        close,
        snap,
        showLoader,
        hideLoader,
        updateContext,
        lock,
        unlock,
        getIcon,
        setIcon,
        setFrameColor,
        setTabTooltip,
        getTabTooltip,
        attachTab,
        detachTab,
        setTabHeaderVisible,
        showPopup,
        createFlydown,
        setModalState,
        setZoomFactor,
        zoomIn,
        zoomOut,
        showDevTools,
        capture,
        flash,
        flashTab,
        setSticky,
        print,
        printToPDF,
        place,
        ungroup,
        refresh,
        goBack,
        goForward,
        download,
        configure,
        getConfiguration,
        getChannel: async () => {
            const wins = await getChannels().getWindowsWithChannels({ windowIds: [_id] });
            return wins[0]?.channel;
        },
        startDrag,
        showDialog,

        onClose,
        onUrlChanged,
        onTitleChanged,
        onFrameButtonAdded,
        onFrameButtonRemoved,
        onFrameButtonClicked,
        onCollapsed,
        onExpanded,
        onMinimized,
        onMaximized,
        onNormal,
        onAttached,
        onDetached,
        onVisibilityChanged,
        onContextUpdated,
        onLockingChanged,
        onBoundsChanged,
        onFrameColorChanged,
        onFocusChanged,
        onStickyChanged,
        onGroupChanged,
        onWindowAttached,
        onWindowDetached,
        onTabSelectionChanged,
        onTabHeaderVisibilityChanged,
        onClosing,
        onRefreshing,
        onZoomFactorChanged,
        onPlacementSettingsChanged,
        onNeighboursChanged,
        onNavigating,

        get tabs() {
            return getAllTabs();
        },

        get isTabHeaderVisible() {
            return _isTabHeaderVisible;
        },

        get isTabSelected() {
            return _isTabSelected;
        },

        // @glue42/web compatibility layer
        getURL() {
            return Promise.resolve(_url);
        },

        getTitle() {
            return Promise.resolve(_title);
        },

        getBounds(): Promise<Glue42.Windows.Bounds> {
            return Promise.resolve(_bounds);
        },

        getContext(): Promise<any> {
            return Promise.resolve(_context);
        },

        setContext(context: any): Promise<GDWindow> {
            if (util.isUndefinedOrNull(context)) {
                throw new Error(`"context" must not be null or undefined, set to empty object if you want to clear it out.`);
            }

            return executor.updateContext(resultWindow, context, true);
        },

        getDisplay(): Promise<Glue42.Displays.Display> {
            const displayAPI = displayAPIGetter();
            return displayAPI.getByWindowId(id);
        },

        resizeTo(width?: number, height?: number): Promise<GDWindow> {
            return moveResize({ width, height });
        },

        moveTo(top?: number, left?: number): Promise<GDWindow> {
            return moveResize({ top, left });
        },

        async getParentWindow() {
            const myParentId = (_settings as any).parentInstanceId;
            if (!myParentId) {
                return undefined;
            }
            return windowStore.list[myParentId]?.API;
        },

        async getChildWindows() {
            return Object.keys(windowStore.list)
                .map((key) => windowStore.list[key].API)
                .filter((w) => {
                    const parentId = (w.settings as any).parentInstanceId;
                    return parentId === id;
                });
        },

        joinChannel: (name: string) => {
            return getChannels().join(name, id);
        },

        leaveChannel: () => {
            return getChannels().leave(id);
        }
    };

    // #endregion "API"

    // #endregion "Group Events"

    const events: WindowEvents = {
        handleUpdate,
        handleWindowClose,
        handleWindowChangeState,
        handleTitleChanged,
        handleVisibilityChanged,
        handleUrlChanged,
        handleWindowSettingsChanged,
        handleContextUpdated,
        handleFrameIsLockedChanged,
        handleBoundsChanged,
        handleFocusChanged,
        handleFrameButtonAdded,
        handleFrameButtonRemoved,
        handleFrameButtonClicked,
        handleFrameColorChanged,
        handleFrameAttached,
        handleFrameSelectionChanged,
        handleCompositionChanged,
        handleGroupHeaderVisibilityChanged,
        handleTabHeaderVisibilityChanged,
        handleGroupChanged,
        handleAttached,
        handleDetached,
        handleWindowAttached,
        handleWindowDetached,
        handleZoomFactorChanged,
        handleIsStickyChanged,
        handlePlacementSettingsChanged
    };

    const groupArgs: GroupCreationArgs = {
        get isGroupHibernated() {
            return _isGroupHibernated;
        },
        get isGroupVisible() {
            return _isGroupVisible;
        },
    }

    return {
        API: resultWindow,
        Events: events,
        GroupCreationArgs: groupArgs
    };
};
